import { Checkbox, IconButton, InputAdornment, styled, Tooltip } from '@mui/material';
import { SearchInput } from 'Components/Forms/SearchToolbar';
import FlexBox from 'Components/FlexBox';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import ThemeTypography from 'DesignSystem/Primitives/Typography/ThemeTypography';
import React from 'react';
import { Tbooleanish, Booleanish } from 'types/booleanish.types';
import { SCROLLBAR_SPACING, TABLE_HORIZONTAL_PADDING } from './table.constants';
import { IOrderBy, TBaseTableTypes, TTableColumn } from './table.types';
import { TRequiredColumnProps } from './TableCell';

type TTableHeaderProps<TColumKey extends string | TBaseTableTypes = string> = {
  'data-testid'?: string;
  columns: (TRequiredColumnProps & TTableColumn<TColumKey>)[];
  searchPlaceholder?: string;
  searchTerm?: string | null;
  hideSearchbar?: boolean;
  selectAllChecked?: boolean;
  disableSelectAll?: boolean;
  orderBy?: IOrderBy<TColumKey>;
  addScrollbarPadding?: boolean;
  onSearchTermChange?: (value: string) => void;
  /**
   * Only called when a sortable column header is clicked
   */
  onOrderByChange?: (newOrderBy?: IOrderBy<TColumKey>) => void;
  onSelectionAllClick?: () => void;
};

const StyledTableHeader = styled('div')<{ 'add-scrollbar-padding': Tbooleanish }>(
  ({ theme, 'add-scrollbar-padding': addScrollbarPadding }) => ({
    display: 'flex',
    width: `calc(100% + ${TABLE_HORIZONTAL_PADDING}px - ${
      addScrollbarPadding === 'true' ? SCROLLBAR_SPACING : 0
    }px )`,
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(5),
  })
);

const StyledTableCell = styled(FlexBox)<{ clickable: Tbooleanish; highlighted: Tbooleanish }>(
  ({ clickable, highlighted }) => ({
    '& .MuiTypography-root': {
      fontWeight: highlighted === 'true' ? '500 !important' : 'inherit',
    },
    '&:hover': {
      '& button': {
        display: 'block',
      },
    },
    cursor: clickable === 'true' ? 'pointer' : 'default',
  })
);

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  padding: 0,
  margin: 0,
}));

const SortButton = styled(IconButton)<{ order?: 'ASC' | 'DESC' }>(({ theme, order }) => ({
  ...(order === undefined ? { display: 'none' } : {}),
  ...(order === 'DESC' ? { transform: 'rotate(180deg)' } : {}),
  marginLeft: theme.spacing(1),
  transition: 'transform 0.2s ease-in-out',
}));

const TableHeader = <TColumKey extends string | TBaseTableTypes = string>({
  'data-testid': dataTestId,
  columns,
  searchPlaceholder,
  searchTerm,
  hideSearchbar,
  selectAllChecked,
  disableSelectAll,
  addScrollbarPadding,
  orderBy,
  onSearchTermChange,
  onOrderByChange,
  onSelectionAllClick,
}: TTableHeaderProps<TColumKey>) => {
  // Find the first column that is not the selection column
  const renderSearchbarOnColumnIndex = hideSearchbar
    ? -1
    : columns.findIndex(({ columnKey }) => columnKey !== 'selection' && columnKey !== 'actions');

  return (
    <StyledTableHeader
      data-testid={dataTestId}
      add-scrollbar-padding={Booleanish(addScrollbarPadding)}
    >
      {columns.map(({ columnKey, headerLabel, sortable, width }, index) => {
        // The collapsable column is not rendered in the header
        if (columnKey === 'collapsable') {
          return null;
        }

        // The searchbar will be rendered by default on the first header column that is not the selection column
        if (index === renderSearchbarOnColumnIndex) {
          return (
            <FlexBox
              key={columnKey}
              width={width}
              maxWidth={width}
              m={index === 0 ? `0 8px 0 -16px` : undefined}
            >
              <SearchInput
                delay={500}
                placeholder={searchPlaceholder}
                value={searchTerm}
                setDebouncedState={onSearchTermChange}
                inputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      {!searchTerm?.length ? (
                        <Icon name="search" size="x-small" />
                      ) : (
                        <CloseButton size="small" onClick={() => onSearchTermChange?.('')}>
                          <Icon name="cross" size="x-small" />
                        </CloseButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </FlexBox>
          );
        }

        const isSortedByThisColumn = orderBy?.orderByField === columnKey;

        const handleOrderByChange = () => {
          if (sortable) {
            if (!orderBy) {
              onOrderByChange?.({ orderByField: columnKey, orderByMethod: 'ASC' });
            } else if (isSortedByThisColumn && orderBy.orderByMethod === 'ASC') {
              onOrderByChange?.({ orderByField: columnKey, orderByMethod: 'DESC' });
            } else {
              onOrderByChange?.(undefined);
            }
          }
        };

        return (
          <StyledTableCell
            key={columnKey}
            width={width}
            maxWidth={width}
            highlighted={Booleanish(isSortedByThisColumn)}
            clickable={Booleanish(sortable)}
            onClick={handleOrderByChange}
          >
            {columnKey === 'selection' && (
              <Checkbox
                checked={selectAllChecked}
                disabled={disableSelectAll}
                onChange={onSelectionAllClick}
              />
            )}
            {headerLabel && (
              <ThemeTypography variant="HEADER_5" color="GRAY_100" autoOverflow>
                {headerLabel}
              </ThemeTypography>
            )}
            {sortable && (
              <Tooltip
                title={
                  !isSortedByThisColumn
                    ? 'Sort in ascending order'
                    : orderBy.orderByMethod === 'ASC'
                    ? 'Sort in descending order'
                    : 'Remove sorting'
                }
                placement="bottom"
              >
                <SortButton
                  size="small"
                  order={isSortedByThisColumn ? orderBy.orderByMethod : undefined}
                  onClick={handleOrderByChange}
                >
                  <Icon name="arrow-line-down" size="small" />
                </SortButton>
              </Tooltip>
            )}
          </StyledTableCell>
        );
      })}
    </StyledTableHeader>
  );
};

export default TableHeader;
